import { useState, useCallback } from 'react';
import Iconify from '@syntra/ui/components/iconify';
import { useSnackbar } from '@syntra/ui/components/snackbar';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { IUserPage } from 'src/domains/user-account/types/user-account';
import { useGetUserAccount, updateActiveUserPage } from 'src/domains/user-account/api';

// ----------------------------------------------------------------------

export default function WorkingPageSelector() {
  const { userAccount, userAccountLoading, refreshUserAccount } = useGetUserAccount();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const workingPages = userAccount?.working_user_pages || [];
  const activePageId = userAccount?.active_user_page?.documentId || '';

  const handlePageChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newPageId = event.target.value;

      if (newPageId === activePageId) return;

      try {
        setLoading(true);
        await updateActiveUserPage(newPageId);
        await refreshUserAccount();
        enqueueSnackbar('Working page switched successfully', { variant: 'success' });
      } catch (error) {
        console.error('Failed to switch working page:', error);
        enqueueSnackbar('Failed to switch working page', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [activePageId, refreshUserAccount, enqueueSnackbar]
  );

  // Don't show if user has no working pages or only one
  if (userAccountLoading || !workingPages || workingPages.length <= 1) {
    return null;
  }

  return (
    <Box sx={{ minWidth: { xs: 180, sm: 220 }, maxWidth: 280 }}>
      <TextField
        select
        fullWidth
        size="small"
        value={activePageId}
        onChange={handlePageChange}
        disabled={loading}
        SelectProps={{
          renderValue: (selected) => {
            const selectedPage = workingPages.find((page) => page.documentId === selected);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify
                  icon={
                    selectedPage?.type === 'business' ? 'solar:buildings-2-bold' : 'solar:user-bold'
                  }
                  width={18}
                />
                <Typography variant="body2" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {selectedPage?.display_name || selectedPage?.name}
                </Typography>
              </Box>
            );
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.neutral',
          },
        }}
      >
        {workingPages.map((page: IUserPage) => (
          <MenuItem key={page.documentId} value={page.documentId}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <Iconify
                icon={page.type === 'business' ? 'solar:buildings-2-bold' : 'solar:user-bold'}
                width={20}
              />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {page.display_name || page.name}
                </Typography>
                {page.company && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {page.company.name}
                  </Typography>
                )}
              </Box>
              {page.documentId === activePageId && (
                <Chip label="Active" size="small" color="primary" />
              )}
            </Box>
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
